import React, { useState, useMemo, useCallback, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, DollarSign, Plus, Trash2, X, Calendar, Eye } from 'lucide-react';
import { useApp } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente memoizado para tarjeta de material
const MaterialCard = memo(({ entrega, onVerDetalles, onToggleCompletado, onDelete }) => (
  <div className="bg-white border-2 border-amber-200 rounded-xl p-5 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-bold text-gray-800">{entrega.cliente}</h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          entrega.completado ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
        }`}>
          {entrega.completado ? 'Completado' : 'Pendiente'}
        </span>
      </div>
      <button onClick={() => onVerDetalles(entrega)} className="text-amber-600 hover:text-amber-700 flex items-center gap-1">
        <Eye size={16} /> Ver detalles
      </button>
    </div>
    <div className="mb-2">
      <p className="text-gray-700 text-lg">{entrega.descripcion || entrega.items?.[0]?.descripcion || '-'}</p>
      <p className="text-gray-500 text-sm">
        Cantidad: {entrega.cantidad || entrega.items?.[0]?.cantidad || '-'} {entrega.unidad || entrega.items?.[0]?.unidad || ''} 📅 {entrega.fechaCreacion ? new Date(entrega.fechaCreacion).toLocaleDateString('es-CO') : '-'}
      </p>
      {entrega.numeroFactura && <p className="text-gray-500 text-sm">Factura: {entrega.numeroFactura}</p>}
    </div>
    <hr className="border-amber-200 my-4" />
    {!entrega.completado ? (
      <button onClick={() => onToggleCompletado(entrega.id, false)} className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition-colors">
        Marcar como Completado
      </button>
    ) : (
      <>
        <div className="w-full py-3 bg-green-500 text-white rounded-lg font-semibold flex items-center justify-center gap-2 cursor-default">✓ Completado</div>
        <div className="mt-3 p-3 border border-green-200 rounded-lg bg-green-50">
          <p className="text-green-600 font-medium">📋 Completado exitosamente</p>
          <p className="text-gray-600 text-sm">{entrega.fechaCompletado ? new Date(entrega.fechaCompletado).toLocaleString('es-CO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</p>
          <p className="text-gray-600 text-sm">Por: {entrega.usuarioCompletado || 'Sistema'}</p>
        </div>
      </>
    )}
    <div className="mt-3 flex justify-end">
      <button onClick={() => onDelete(entrega.id)} className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1">
        <Trash2 size={16} /> Eliminar
      </button>
    </div>
  </div>
));

// Componente memoizado para tarjeta de saldo
const SaldoCard = memo(({ saldo, onVerDetalles, onToggleCompletado, onDelete }) => {
  const fechaCreacion = saldo.fechaCreacion ? new Date(saldo.fechaCreacion) : new Date();
  const diasPendientes = Math.floor((new Date() - fechaCreacion) / (1000 * 60 * 60 * 24));
  
  return (
    <div className="bg-white border-2 border-green-200 rounded-xl p-5 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-gray-800">{saldo.cliente}</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            saldo.completado ? 'bg-green-500 text-white' : diasPendientes > 30 ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
            {saldo.completado ? 'Pagado' : diasPendientes > 30 ? 'Más de 30 días' : 'Pendiente'}
          </span>
        </div>
        <button onClick={() => onVerDetalles(saldo)} className="text-green-600 hover:text-green-700 flex items-center gap-1">
          <Eye size={16} /> Ver detalles
        </button>
      </div>
      <p className="text-3xl font-bold text-green-600 mb-2">${parseFloat(saldo.monto).toLocaleString('es-CO', { minimumFractionDigits: 2 })}</p>
      <div className="flex items-center gap-4 text-gray-500 mb-4">
        <span>Factura: {saldo.concepto || '-'}</span>
        {!saldo.completado && <span className="text-red-500">📅 {diasPendientes} días pendientes</span>}
      </div>
      <hr className="border-green-200 my-4" />
      {!saldo.completado ? (
        <button onClick={() => onToggleCompletado(saldo.id, false)} className="w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors">
          Marcar como Completado
        </button>
      ) : (
        <>
          <div className="w-full py-3 bg-green-500 text-white rounded-lg font-semibold flex items-center justify-center gap-2 cursor-default">✓ Completado</div>
          <div className="mt-3 p-3 border border-green-200 rounded-lg bg-green-50">
            <p className="text-green-600 font-medium">📋 Completado exitosamente</p>
            <p className="text-gray-600 text-sm">{saldo.fechaCompletado ? new Date(saldo.fechaCompletado).toLocaleString('es-CO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</p>
            <p className="text-gray-600 text-sm">Por: {saldo.usuarioCompletado || 'Sistema'}</p>
          </div>
        </>
      )}
      <div className="mt-3 flex justify-end">
        <button onClick={() => onDelete(saldo.id)} className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1">
          <Trash2 size={16} /> Eliminar
        </button>
      </div>
    </div>
  );
});

const GestionPendientes = () => {
  const { db, saldosPendientes: preloadedSaldos } = useApp();
  const [mainTab, setMainTab] = useState('material');
  
  // Material Pendiente
  const [materialTab, setMaterialTab] = useState('pendiente');
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [selectedEntrega, setSelectedEntrega] = useState(null);
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialForm, setMaterialForm] = useState({
    cliente: '',
    numeroFactura: '',
    items: [{ id: 1, descripcion: '', cantidad: '', unidad: '', estado: 'Pendiente' }]
  });

  // Saldos Pendientes
  const [saldosTab, setSaldosTab] = useState('pendiente');
  const [showSaldoModal, setShowSaldoModal] = useState(false);
  const [showSaldoDetalleModal, setShowSaldoDetalleModal] = useState(false);
  const [selectedSaldo, setSelectedSaldo] = useState(null);
  const [editingSaldoId, setEditingSaldoId] = useState(null);
  const [saldoSearch, setSaldoSearch] = useState('');
  const [saldoForm, setSaldoForm] = useState({
    cliente: '', concepto: '', monto: '', fecha_vencimiento: '', observaciones: ''
  });

  // React Query para material pendiente - usa datos precargados como initialData
  const { data: entregas = [], refetch: refetchEntregas } = useQuery({
    queryKey: ['material-pendiente'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/material-pendiente`);
      const data = await res.json();
      return data.success ? (data.entregas || []) : [];
    },
    initialData: db?.materialesPendientes?.length > 0 ? db.materialesPendientes : undefined,
    staleTime: 60 * 1000,
  });

  // React Query para saldos pendientes - usa datos precargados como initialData
  const { data: saldos = [], refetch: refetchSaldos } = useQuery({
    queryKey: ['saldos-pendientes'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/saldos-pendientes`);
      const data = await res.json();
      return data.success ? (data.saldos || []) : [];
    },
    initialData: preloadedSaldos?.length > 0 ? preloadedSaldos : undefined,
    staleTime: 60 * 1000,
  });

  const resetMaterialForm = () => {
    setMaterialForm({
      cliente: '',
      numeroFactura: '',
      items: [{ id: 1, descripcion: '', cantidad: '', unidad: '', estado: 'Pendiente' }]
    });
  };

  const addItem = () => {
    setMaterialForm({
      ...materialForm,
      items: [...materialForm.items, { id: Date.now(), descripcion: '', cantidad: '', unidad: '', estado: 'Pendiente' }]
    });
  };

  const removeItem = (itemId) => {
    if (materialForm.items.length > 1) {
      setMaterialForm({
        ...materialForm,
        items: materialForm.items.filter(item => item.id !== itemId)
      });
    }
  };

  const updateItem = (itemId, field, value) => {
    setMaterialForm({
      ...materialForm,
      items: materialForm.items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    });
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    
    const formData = {...materialForm};
    const isEditing = editingMaterialId;
    
    // Cerrar modal inmediatamente
    setShowMaterialModal(false);
    setEditingMaterialId(null);
    resetMaterialForm();
    
    try {
      if (isEditing) {
        await fetch(`${API_URL}/api/material-pendiente/${isEditing}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
      } else {
        const promises = formData.items
          .filter(item => item.descripcion.trim())
          .map(item => {
            const entregaIndividual = {
              cliente: formData.cliente,
              numeroFactura: formData.numeroFactura,
              descripcion: item.descripcion,
              cantidad: item.cantidad,
              unidad: item.unidad,
              estado: item.estado || 'Pendiente',
              items: [item]
            };
            return fetch(`${API_URL}/api/material-pendiente`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(entregaIndividual)
            });
          });
        await Promise.all(promises);
      }
      refetchEntregas();
    } catch (e) { 
      console.error('Error:', e);
    }
  };

  const toggleMaterialCompletado = useCallback(async (id, completado) => {
    try {
      await fetch(`${API_URL}/api/material-pendiente/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completado: !completado })
      });
      refetchEntregas();
    } catch (e) { 
      console.error('Error:', e);
    }
  }, [refetchEntregas]);

  const deleteMaterial = useCallback(async (id) => {
    if (!window.confirm('¿Eliminar esta entrega?')) return;
    
    try {
      await fetch(`${API_URL}/api/material-pendiente/${id}`, { method: 'DELETE' });
      refetchEntregas();
    } catch (e) { 
      console.error('Error:', e);
    }
  }, [refetchEntregas]);

  const editMaterial = (entrega) => {
    setMaterialForm({
      cliente: entrega.cliente || '',
      numeroFactura: entrega.numeroFactura || '',
      items: entrega.items || [{ id: 1, descripcion: '', cantidad: '', unidad: '', estado: 'Pendiente' }]
    });
    setEditingMaterialId(entrega.id);
    setShowMaterialModal(true);
  };

  // ========== SALDOS PENDIENTES ==========
  const handleSaldoSubmit = async (e) => {
    e.preventDefault();
    
    const isEditing = editingSaldoId;
    const formData = {...saldoForm};
    
    // Cerrar modal inmediatamente
    setShowSaldoModal(false);
    setEditingSaldoId(null);
    setSaldoForm({ cliente: '', concepto: '', monto: '', fecha_vencimiento: '', observaciones: '' });
    
    try {
      const url = isEditing 
        ? `${API_URL}/api/saldos-pendientes/${isEditing}`
        : `${API_URL}/api/saldos-pendientes`;
      await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      refetchSaldos();
    } catch (e) { 
      console.error('Error:', e);
    }
  };

  const toggleSaldoCompletado = useCallback(async (id, completado) => {
    try {
      await fetch(`${API_URL}/api/saldos-pendientes/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completado: !completado })
      });
      refetchSaldos();
    } catch (e) { 
      console.error('Error:', e);
    }
  }, [refetchSaldos]);

  const deleteSaldo = useCallback(async (id) => {
    if (!window.confirm('¿Eliminar este saldo?')) return;
    
    try {
      await fetch(`${API_URL}/api/saldos-pendientes/${id}`, { method: 'DELETE' });
      refetchSaldos();
    } catch (e) { 
      console.error('Error:', e);
    }
  }, [refetchSaldos]);

  const editSaldo = (saldo) => {
    setSaldoForm({
      cliente: saldo.cliente, concepto: saldo.concepto,
      monto: saldo.monto, fecha_vencimiento: saldo.fecha_vencimiento,
      observaciones: saldo.observaciones || ''
    });
    setEditingSaldoId(saldo.id);
    setShowSaldoModal(true);
  };

  // Función de filtrado por búsqueda
  const filterBySearch = (items, search, fields, searchInItems = false) => {
    if (!search.trim()) return items;
    const searchLower = search.toLowerCase();
    return items.filter(item => {
      // Buscar en campos principales
      const matchInFields = fields.some(field => {
        const value = item[field];
        return value && value.toString().toLowerCase().includes(searchLower);
      });
      
      if (matchInFields) return true;
      
      // Buscar en descripción directa del item
      if (searchInItems && item.descripcion) {
        if (item.descripcion.toString().toLowerCase().includes(searchLower)) {
          return true;
        }
      }
      
      // Buscar en array de items (descripción de cada ítem)
      if (searchInItems && item.items && Array.isArray(item.items)) {
        return item.items.some(subItem => 
          subItem.descripcion && subItem.descripcion.toString().toLowerCase().includes(searchLower)
        );
      }
      
      return false;
    });
  };

  // Datos filtrados con búsqueda
  const materialPendientes = filterBySearch(
    entregas.filter(e => !e.completado), 
    materialSearch, 
    ['cliente', 'numeroFactura'],
    true // Habilitar búsqueda en ítems
  );
  const materialCompletados = filterBySearch(
    entregas.filter(e => e.completado), 
    materialSearch, 
    ['cliente', 'numeroFactura'],
    true // Habilitar búsqueda en ítems
  );
  const saldosPendientes = filterBySearch(
    saldos.filter(s => !s.completado), 
    saldoSearch, 
    ['cliente', 'concepto']
  );
  const saldosCompletados = filterBySearch(
    saldos.filter(s => s.completado), 
    saldoSearch, 
    ['cliente', 'concepto']
  );
  const totalPorCobrar = saldosPendientes.reduce((sum, s) => sum + (parseFloat(s.monto) || 0), 0);

  const unidades = ['Unidad', 'Metro', 'M²', 'M³', 'Caja', 'Bulto', 'Kg', 'Tonelada', 'Litro', 'Galón'];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">📋 Gestión de Pendientes</h1>

        {/* Tabs principales */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setMainTab('material')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              mainTab === 'material' ? 'bg-amber-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Package size={20} /> Material Pendiente
          </button>
          <button
            onClick={() => setMainTab('saldos')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              mainTab === 'saldos' ? 'bg-green-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <DollarSign size={20} /> Saldos Pendientes
          </button>
        </div>

        {/* =============== MATERIAL PENDIENTE =============== */}
        {mainTab === 'material' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-amber-600 flex items-center gap-2">
                <Package /> Material Pendiente
              </h2>
              <button
                onClick={() => { setShowMaterialModal(true); setEditingMaterialId(null); resetMaterialForm(); }}
                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} /> Nueva Entrega
              </button>
            </div>

            {/* Buscador */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="🔍 Buscar por cliente, número de factura o ítem..."
                value={materialSearch}
                onChange={(e) => setMaterialSearch(e.target.value)}
                className="w-full p-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setMaterialTab('pendiente')}
                className={`px-4 py-2 rounded-lg font-medium ${materialTab === 'pendiente' ? 'bg-amber-100 text-amber-700 border-2 border-amber-500' : 'bg-gray-100 text-gray-600'}`}
              >
                Material por Entregar ({materialPendientes.length})
              </button>
              <button
                onClick={() => setMaterialTab('completado')}
                className={`px-4 py-2 rounded-lg font-medium ${materialTab === 'completado' ? 'bg-green-100 text-green-700 border-2 border-green-500' : 'bg-gray-100 text-gray-600'}`}
              >
                Completado ({materialCompletados.length})
              </button>
            </div>

            {/* Tarjetas de Material */}
            {(materialTab === 'pendiente' ? materialPendientes : materialCompletados).length === 0 ? (
              <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
                No hay entregas {materialTab === 'pendiente' ? 'pendientes' : 'completadas'}
              </div>
            ) : (
              <div className="space-y-4">
                {(materialTab === 'pendiente' ? materialPendientes : materialCompletados).map(entrega => (
                  <MaterialCard
                    key={entrega.id}
                    entrega={entrega}
                    onVerDetalles={(e) => { setSelectedEntrega(e); setShowDetalleModal(true); }}
                    onToggleCompletado={toggleMaterialCompletado}
                    onDelete={deleteMaterial}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* =============== SALDOS PENDIENTES =============== */}
        {mainTab === 'saldos' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-green-600 flex items-center gap-2">
                  <DollarSign /> Saldos Pendientes
                </h2>
                <p className="text-gray-600">Total por cobrar: <span className="font-bold text-green-600">${totalPorCobrar.toLocaleString('es-CO')}</span></p>
              </div>
              <button
                onClick={() => { setShowSaldoModal(true); setEditingSaldoId(null); setSaldoForm({ cliente: '', concepto: '', monto: '', fecha_vencimiento: '', observaciones: '' }); }}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} /> Nuevo Saldo
              </button>
            </div>

            {/* Buscador */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="🔍 Buscar por cliente o número de factura..."
                value={saldoSearch}
                onChange={(e) => setSaldoSearch(e.target.value)}
                className="w-full p-3 border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setSaldosTab('pendiente')}
                className={`px-4 py-2 rounded-lg font-medium ${saldosTab === 'pendiente' ? 'bg-green-100 text-green-700 border-2 border-green-500' : 'bg-gray-100 text-gray-600'}`}
              >
                Saldos por Cobrar ({saldosPendientes.length})
              </button>
              <button
                onClick={() => setSaldosTab('completado')}
                className={`px-4 py-2 rounded-lg font-medium ${saldosTab === 'completado' ? 'bg-gray-200 text-gray-700 border-2 border-gray-400' : 'bg-gray-100 text-gray-600'}`}
              >
                Completado ({saldosCompletados.length})
              </button>
            </div>

            {/* Tarjetas de Saldos */}
            {(saldosTab === 'pendiente' ? saldosPendientes : saldosCompletados).length === 0 ? (
              <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
                No hay saldos {saldosTab === 'pendiente' ? 'pendientes' : 'completados'}
              </div>
            ) : (
              <div className="space-y-4">
                {(saldosTab === 'pendiente' ? saldosPendientes : saldosCompletados).map(saldo => (
                  <SaldoCard
                    key={saldo.id}
                    saldo={saldo}
                    onVerDetalles={(s) => { setSelectedSaldo(s); setShowSaldoDetalleModal(true); }}
                    onToggleCompletado={toggleSaldoCompletado}
                    onDelete={deleteSaldo}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Material - Nuevo diseño */}
      {showMaterialModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">{editingMaterialId ? 'Editar' : 'Nuevo'} Material Pendiente</h2>
              <button onClick={() => setShowMaterialModal(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            
            <form onSubmit={handleMaterialSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Cliente *</label>
                  <input 
                    type="text" 
                    placeholder="Nombre del cliente" 
                    value={materialForm.cliente} 
                    onChange={e => setMaterialForm({...materialForm, cliente: e.target.value})} 
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400" 
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Número de Factura *</label>
                  <input 
                    type="text" 
                    placeholder="Ej: FAC-001" 
                    value={materialForm.numeroFactura} 
                    onChange={e => setMaterialForm({...materialForm, numeroFactura: e.target.value})} 
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400" 
                    required 
                  />
                </div>
              </div>

              <div className="flex justify-between items-center">
                <label className="text-sm text-gray-300">Ítems de Material</label>
                <button 
                  type="button" 
                  onClick={addItem}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg flex items-center gap-1 text-sm"
                >
                  <Plus size={16} /> Agregar Ítem
                </button>
              </div>

              {materialForm.items.map((item, index) => (
                <div key={item.id} className="border border-slate-600 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">Ítem {index + 1}</span>
                    {materialForm.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Descripción *</label>
                    <textarea 
                      placeholder="Descripción del material a entregar" 
                      value={item.descripcion} 
                      onChange={e => updateItem(item.id, 'descripcion', e.target.value)}
                      className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400" 
                      rows={2}
                      required 
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Cantidad *</label>
                      <input 
                        type="text" 
                        placeholder="Ej: 10" 
                        value={item.cantidad} 
                        onChange={e => updateItem(item.id, 'cantidad', e.target.value)}
                        className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400" 
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Unidad de Medida *</label>
                      <select 
                        value={item.unidad} 
                        onChange={e => updateItem(item.id, 'unidad', e.target.value)}
                        className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                        required
                      >
                        <option value="">Seleccionar</option>
                        {unidades.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Estado *</label>
                      <select 
                        value={item.estado} 
                        onChange={e => updateItem(item.id, 'estado', e.target.value)}
                        className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      >
                        <option value="Pendiente">Pendiente</option>
                        <option value="Entregado">Entregado</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-medium">
                  {editingMaterialId ? 'Actualizar' : 'Crear Material Pendiente'}
                </button>
                <button type="button" onClick={() => setShowMaterialModal(false)} className="px-6 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-medium">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Detalle de Entrega */}
      {showDetalleModal && selectedEntrega && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
            {/* Header */}
            <div className="flex justify-between items-start p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedEntrega.cliente}</h2>
                <p className="text-gray-500">Factura: {selectedEntrega.numeroFactura || '-'}</p>
              </div>
              <button 
                onClick={() => setShowDetalleModal(false)} 
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={24} />
              </button>
            </div>

            {/* Fecha de Venta */}
            <div className="px-6 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <Calendar className="text-blue-500" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Fecha de Venta</p>
                  <p className="text-gray-900 font-medium">
                    {selectedEntrega.fechaCreacion 
                      ? new Date(selectedEntrega.fechaCreacion).toLocaleDateString('es-CO', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Ítems del Pedido */}
            <div className="px-6 pb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📦 Ítems del Pedido</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Descripción</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Cantidad</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Unidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="p-3 text-gray-900">{selectedEntrega.descripcion || selectedEntrega.items?.[0]?.descripcion || '-'}</td>
                      <td className="p-3 text-gray-900">{selectedEntrega.cantidad || selectedEntrega.items?.[0]?.cantidad || '-'}</td>
                      <td className="p-3 text-gray-900">{selectedEntrega.unidad || selectedEntrega.items?.[0]?.unidad || '-'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Estado */}
            <div className="px-6 pb-6">
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                selectedEntrega.completado 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {selectedEntrega.completado ? '✓ Completado' : '⏳ Pendiente'}
              </span>
              
              {selectedEntrega.completado && selectedEntrega.usuarioCompletado && (
                <p className="mt-2 text-sm text-gray-500">
                  Completado por: {selectedEntrega.usuarioCompletado}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Saldo - Diseño actualizado */}
      {showSaldoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">{editingSaldoId ? 'Editar' : 'Nuevo'} Saldo</h2>
              <button onClick={() => setShowSaldoModal(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleSaldoSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Nombre del Cliente *</label>
                <input 
                  type="text" 
                  placeholder="Nombre del cliente" 
                  value={saldoForm.cliente} 
                  onChange={e => setSaldoForm({...saldoForm, cliente: e.target.value})} 
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400" 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Número de Factura *</label>
                <input 
                  type="text" 
                  placeholder="Ej: FAC-001" 
                  value={saldoForm.concepto} 
                  onChange={e => setSaldoForm({...saldoForm, concepto: e.target.value})} 
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400" 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Valor del Saldo *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input 
                    type="number" 
                    placeholder="0.00" 
                    value={saldoForm.monto} 
                    onChange={e => setSaldoForm({...saldoForm, monto: e.target.value})} 
                    className="w-full p-3 pl-8 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400" 
                    required 
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-medium">
                  {editingSaldoId ? 'Actualizar' : 'Guardar Nuevo Saldo'}
                </button>
                <button type="button" onClick={() => setShowSaldoModal(false)} className="px-6 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-medium">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Detalle de Saldo */}
      {showSaldoDetalleModal && selectedSaldo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">Detalles del Saldo</h2>
              <button 
                onClick={() => setShowSaldoDetalleModal(false)} 
                className="text-gray-400 hover:text-white p-1"
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido */}
            <div className="p-5 space-y-4">
              {/* Cliente */}
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">Cliente</p>
                <p className="text-white text-lg font-medium">{selectedSaldo.cliente}</p>
              </div>

              {/* Número de Factura */}
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">Número de Factura</p>
                <p className="text-white text-lg font-medium">{selectedSaldo.concepto || '-'}</p>
              </div>

              {/* Valor del Saldo */}
              <div className="bg-green-600 rounded-lg p-4">
                <p className="text-green-100 text-sm mb-1">Valor del Saldo</p>
                <p className="text-white text-2xl font-bold">
                  ${parseFloat(selectedSaldo.monto).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                </p>
              </div>

              {/* Fecha de Registro */}
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-1">Fecha de Registro</p>
                <p className="text-white text-lg font-medium">
                  {selectedSaldo.fechaCreacion 
                    ? new Date(selectedSaldo.fechaCreacion).toLocaleDateString('es-CO', {
                        year: 'numeric', month: 'long', day: 'numeric'
                      })
                    : '-'}
                </p>
              </div>

              {/* Estado */}
              <div className="bg-slate-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-2">Estado</p>
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                  selectedSaldo.completado 
                    ? 'bg-green-500 text-white' 
                    : 'bg-amber-500 text-white'
                }`}>
                  {selectedSaldo.completado ? '✓ Pagado' : '⏳ Pendiente de Pago'}
                </span>
              </div>
            </div>

            {/* Botón Cerrar */}
            <div className="p-5 border-t border-slate-700">
              <button 
                onClick={() => setShowSaldoDetalleModal(false)}
                className="w-full py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionPendientes;
